import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, UI_TRANSLATIONS } from '../utils/translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('de');

  useEffect(() => {
    const savedLang = localStorage.getItem('preferred_language') as Language | null;
    if (savedLang === 'de' || savedLang === 'en') {
      setLanguageState(savedLang);
      document.documentElement.lang = savedLang;
    } else {
      setLanguageState('de');
      document.documentElement.lang = 'de';
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('preferred_language', lang);
    document.documentElement.lang = lang;
  };

  const t = (key: string, fallback?: string): string => {
    if (UI_TRANSLATIONS[key] && UI_TRANSLATIONS[key][language]) {
      return UI_TRANSLATIONS[key][language];
    }
    return fallback || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    return {
      language: 'de',
      setLanguage: () => {},
      t: (key: string, fallback?: string) => {
        if (UI_TRANSLATIONS[key] && UI_TRANSLATIONS[key]['de']) {
          return UI_TRANSLATIONS[key]['de'];
        }
        return fallback || key;
      }
    };
  }
  return context;
};
